import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Building2, Sprout, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";

function DebateResult({ index, label, stat }) {
  const { t } = useTranslation();
  const total = stat.total || 0;
  const ecolePct = total ? Math.round((stat.ecole / total) * 100) : 50;
  const coconPct = total ? 100 - ecolePct : 50;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="glass frame-pulse rounded-2xl p-6"
      data-testid={`arene-result-${stat.debate_id}`}
    >
      <p className="text-center text-sm text-foreground/70 leading-snug mb-6">
        <span className="font-display text-primary/80 tabular-nums mr-1.5">{String(index + 1).padStart(2, "0")}</span>
        {label}
      </p>

      <div className="grid grid-cols-2 gap-4 mb-5">
        {/* École */}
        <div className="text-center" data-testid={`arene-result-${stat.debate_id}-ecole`}>
          <Building2 size={20} className="text-slate-300/70 mx-auto mb-2" />
          <p className="font-display text-4xl sm:text-5xl text-slate-200 tabular-nums leading-none">{stat.ecole}</p>
          <p className="text-xs text-slate-300/60 mt-2">{t("arene.ecoleLabel")}</p>
          <p className="text-xs text-slate-300/45 tabular-nums">{ecolePct}%</p>
        </div>
        {/* Cocoon */}
        <div className="text-center" data-testid={`arene-result-${stat.debate_id}-cocon`}>
          <Sprout size={20} className="text-primary/70 mx-auto mb-2" />
          <p className="font-display text-4xl sm:text-5xl text-gradient-orange tabular-nums leading-none">{stat.cocon}</p>
          <p className="text-xs text-primary/70 mt-2">{t("arene.cocoonLabel")}</p>
          <p className="text-xs text-primary/50 tabular-nums">{coconPct}%</p>
        </div>
      </div>

      {/* slim gauge */}
      <div className="h-2.5 rounded-full overflow-hidden flex bg-background/40 border border-white/10">
        <motion.div
          className="h-full bg-slate-400/60"
          initial={{ width: "50%" }}
          whileInView={{ width: `${ecolePct}%` }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut", delay: index * 0.08 + 0.2 }}
        />
        <motion.div
          className="h-full bg-gradient-to-r from-primary/80 to-primary flex-1"
          initial={{ width: "50%" }}
          whileInView={{ width: `${coconPct}%` }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut", delay: index * 0.08 + 0.2 }}
        />
      </div>
      <p className="text-center text-xs text-foreground/45 mt-3" data-testid={`arene-result-${stat.debate_id}-total`}>
        {total > 0 ? t("arene.totalVotes", { count: total }) : t("arene.noVotes")}
      </p>
    </motion.div>
  );
}

export function AreneEvolution() {
  const { t } = useTranslation();
  const debates = t("arene.debates", { returnObjects: true }) || [];
  const [tally, setTally] = useState(null);

  useEffect(() => {
    api.get("/debates/tally").then(({ data }) => {
      const map = {};
      (data.debates || []).forEach((d) => { map[d.debate_id] = d; });
      setTally(map);
    }).catch(() => setTally({}));
  }, []);

  if (!debates.length) return null;

  return (
    <section className="max-w-4xl mx-auto px-6 pb-24 -mt-6" data-testid="arene-results-section">
      <div className="text-center mb-9">
        <h3 className="font-display text-gradient-orange text-2xl sm:text-3xl inline-flex items-center gap-2 justify-center">
          <BarChart3 size={24} className="text-primary/80" /> {t("arene.resultsTitle")}
        </h3>
        <p className="mt-4 text-foreground/55 leading-relaxed max-w-2xl mx-auto text-sm">{t("arene.resultsSubtitle")}</p>
      </div>

      {tally === null ? (
        <div className="h-[200px] flex items-center justify-center text-foreground/40 text-sm">…</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5" data-testid="arene-results-grid">
          {debates.map((d, i) => (
            <DebateResult
              key={d.id}
              index={i}
              label={d.question}
              stat={tally[d.id] || { debate_id: d.id, ecole: 0, cocon: 0, total: 0 }}
            />
          ))}
        </div>
      )}
    </section>
  );
}
